// react
import { useContext, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

// tracking
import { TrackingEvent, useTracking } from 'hooks';

// components
import { DirectorySearch } from '@karehero/llama/components/organisms/DirectorySearch/DirectorySearch';
import { PageTemplate } from '@karehero/llama/pwa/templates/PageTemplate/PageTemplate';
import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';

import Loading from '@karehero/llama/components/pages/Loading/Loading';

// context
import { DirectoryProfileView } from './DirectoryProfile';
import { DirectoryTrackingEvent } from '@karehero/models';
import { DirectoryInteractionsContext } from '@karehero/llama/contexts/DirectoryInteractions';

// hooks
import {
  useSearchCareHomesQuery,
  useSearchHomeCareQuery,
  buildCareHomeParams,
  buildHomeCareParams,
} from 'store/api/findCare';

const mergeItems = (items1: any[], items2: any[]) => {
  const results: any[] = [];
  const used: { [key: string]: boolean } = {};

  [...items1, ...items2].forEach((item) => {
    if (used[item.regulatoryId]) return;

    results.push(item);
    used[item.regulatoryId] = true;
  });

  return results;
};

export const DirectorySearchView = () => {
  // context
  const { request, setResults, reset } = useContext(
    DirectoryInteractionsContext,
  );

  // hooks
  const { track } = useTracking();
  const [searchParams] = useSearchParams();

  const location = searchParams.get('search');

  const initialLocationRef = useRef(location);

  useEffect(() => {
    initialLocationRef.current = location;
    reset({
      searchTerm: location,
      request: {
        searchTerm: location,
        filters: {},
        page: 1,
      },
    });
  }, [location, reset]);

  // sync search term to URL bar without going through React Router.
  // skip if the request matches the initial URL param or hasn't changed —
  // only write when the user performs a new search via the input field.
  useEffect(() => {
    if (!request?.searchTerm) return;
    if (request.searchTerm === initialLocationRef.current) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get('search') === request.searchTerm) return;
    url.searchParams.set('search', request.searchTerm);
    window.history.replaceState(window.history.state, '', url.toString());
  }, [request?.searchTerm]);

  const careType = request?.filters?.careType?.selectedValue || 'careHome';
  const isCareHome = careType === 'careHome';
  const hasRequest = request?.searchTerm && request?.filters;

  const careHomeParams =
    hasRequest && isCareHome ? buildCareHomeParams(request) : undefined;
  const homeCareParams =
    hasRequest && !isCareHome ? buildHomeCareParams(request) : undefined;

  const {
    data: careHomeResponse,
    isLoading: isCareHomeLoading,
    isFetching: isCareHomeFetching,
  } = useSearchCareHomesQuery(careHomeParams!, {
    skip: !careHomeParams,
  });

  const {
    data: homeCareResponse,
    isLoading: isHomeCareLoading,
    isFetching: isHomeCareFetching,
  } = useSearchHomeCareQuery(homeCareParams!, {
    skip: !homeCareParams,
  });

  const searchResponse = isCareHome ? careHomeResponse : homeCareResponse;
  const isSearchResultsLoading = isCareHome
    ? isCareHomeLoading
    : isHomeCareLoading;
  const isSearchResultsFetching = isCareHome
    ? isCareHomeFetching
    : isHomeCareFetching;

  useEffect(() => {
    if (
      !request ||
      !request.searchTerm ||
      !searchResponse ||
      isSearchResultsLoading
    )
      return;

    if (request.page && request.page > 1) {
      setResults((res: any) => ({
        ...res,
        items: mergeItems(res.items, searchResponse.items),
      }));
    } else {
      setResults(searchResponse);
    }
  }, [request, isSearchResultsLoading, searchResponse, setResults]);

  useEffect(() => {
    track(DirectoryTrackingEvent.DirectoryViewed, {});
  }, [track]);

  useEffect(() => {
    if (!request || request.length === 0) return;

    track(DirectoryTrackingEvent.DirectorySearched, request);
  }, [request, track]);

  // show loading when:
  // - fetching results for a new search term (not pagination)
  // - URL has a search param but reset() hasn't synced yet
  const isPaginating = request?.page > 1;
  if (
    (isSearchResultsFetching && !isPaginating) ||
    (location && (!request || request.searchTerm !== location))
  ) {
    return <Loading />;
  }

  return (
    <DirectorySearch
      bannerImageUrl={'/images/directory-hero.jpg'}
      results={searchResponse || { items: [] }}
    />
  );
};

export const Directory = () => {
  // hooks
  const [searchParams] = useSearchParams();
  const { track } = useTracking();

  const haveProviderId = !!searchParams.get('provider');

  // effects
  useEffect(() => {
    track(TrackingEvent.DirectoryViewed, {});
  }, [track]);

  return (
    <PageTemplate
      headerSlot={
        <PageHeader
          title='Care Search'
          description='Search and compare care providers in your area.'
        />
      }
      contentSlot={
        haveProviderId ? <DirectoryProfileView /> : <DirectorySearchView />
      }
    />
  );
};

export default Directory;
